import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getApiInstance } from "../../services/api";
import {
  recordDistributionGeometry,
  recordStandingPercent,
  type RecordComparisonRequest,
  type RecordComparisonResult,
} from "../../utils/recordComparison";
import "./record-comparison.css";

export function RecordComparisonPanel({
  userId,
  params,
  interval,
  rangeLabel,
}: {
  userId: number;
  params: RecordComparisonRequest;
  interval: string;
  rangeLabel: string;
}) {
  const { t, i18n } = useTranslation();
  const tr = (key: string) => t(`recordComparison.${key}`);
  const [scope, setScope] = useState("selected");
  const [gender, setGender] = useState("all");
  const [age, setAge] = useState("all");
  const [attempt, setAttempt] = useState(0);
  const key = JSON.stringify([userId, params, scope, gender, age, attempt]);
  const [state, setState] = useState<{
    key: string;
    data?: RecordComparisonResult;
    error?: boolean;
  }>({ key: "" });
  useEffect(() => {
    let active = true;
    const { peak_type, metric_type, start_date, end_date, workout_type_id } =
      params;
    getApiInstance()
      .get<{ data: RecordComparisonResult }>(
        `/api/v1/mobile/records/comparison/user/${userId}`,
        {
          params: {
            peak_type,
            metric_type,
            start_date,
            end_date,
            workout_type_id,
            comparison_scope: scope,
            gender,
            age_group: age,
          },
        }
      )
      .then((res) => {
        if (active) setState({ key, data: res.data.data });
      })
      .catch(() => {
        if (active) setState({ key, error: true });
      });
    return () => {
      active = false;
    };
  }, [key]);
  const data = state.key === key ? state.data : undefined;
  const error = state.key === key && state.error;
  const ready = data?.status === "ready";
  const locale = i18n.language;
  const n = (v: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(v);
  const pace = params.peak_type.startsWith("pace");
  const unit = pace
    ? tr("paceUnit")
    : params.metric_type === "wkg"
    ? "W/kg"
    : tr("watts");
  const value = (v: number) =>
    pace
      ? `${n(Math.floor(Math.round(v) / 60))}:${new Intl.NumberFormat(locale, {
          minimumIntegerDigits: 2,
          useGrouping: false,
        }).format(Math.round(v) % 60)}`
      : new Intl.NumberFormat(locale, {
          maximumFractionDigits: params.metric_type === "wkg" ? 2 : 0,
        }).format(v);
  const geometry = data ? recordDistributionGeometry(data) : null;
  const bubbleX = geometry
    ? Math.max(3, Math.min(245, geometry.marker - 56))
    : 0;
  return (
    <section className="record-comparison" aria-label={tr("title")}>
      <header>
        <h3>{tr("title")}</h3>
        <span className="record-comparison-mark" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
      </header>
      <p className="record-comparison-context">
        {tr(pace ? "runningPace" : "cyclingPower")} · {interval}
      </p>
      <div className="record-comparison-toggle">
        {["selected", "all_time"].map((v) => (
          <button
            type="button"
            key={v}
            aria-pressed={v === scope}
            onClick={() => setScope(v)}
          >
            {tr(v)}
          </button>
        ))}
      </div>
      <div className="record-comparison-filters">
        <label>
          {tr("gender")}
          <select value={gender} onChange={(e) => setGender(e.target.value)}>
            {["all", "female", "male"].map((v) => (
              <option key={v} value={v}>
                {tr(v)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {tr("age")}
          <select value={age} onChange={(e) => setAge(e.target.value)}>
            {[
              "all",
              "18-24",
              "25-29",
              "30-34",
              "35-39",
              "40-44",
              "45-49",
              "50-54",
              "55-59",
              "60-64",
              "65-69",
              "70-74",
              "75+",
            ].map((v) => (
              <option key={v} value={v}>
                {v === "all" ? tr("allAges") : v}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="record-comparison-caption">
        {tr(scope === "selected" ? "samePeriod" : "allPeriod").replace(
          "{period}",
          rangeLabel
        )}
      </p>
      {error ? (
        <div className="record-comparison-message">
          <p>{tr("error")}</p>
          <button type="button" onClick={() => setAttempt((v) => v + 1)}>
            {tr("retry")}
          </button>
        </div>
      ) : !data ? (
        <p role="status">{tr("loading")}</p>
      ) : !ready ? (
        <div className="record-comparison-message">
          <h4>{tr(data.status === "no_record" ? "noRecord" : "smallTitle")}</h4>
          <p>
            {tr(data.status === "no_record" ? "noRecordCopy" : "smallCopy")}
          </p>
        </div>
      ) : (
        <>
          <div className="record-comparison-summary" aria-live="polite">
            {data.is_preliminary && (
              <span className="record-comparison-preliminary">
                {tr("preliminary")}
              </span>
            )}
            <small>{tr("yourRecord")}</small>
            <strong className="record-comparison-value">
              <bdi>{value(data.own_value!)}</bdi>
            </strong>
            <small>{unit}</small>
            <p className="record-comparison-standing">
              {tr("standingBefore")}{" "}
              <strong className="record-comparison-score">
                {n(recordStandingPercent(data))}
                <span>{tr("percentSign")}</span>
              </strong>{" "}
              {tr("standingAfter")}
            </p>
            <p className="record-comparison-support">
              {tr("betterCopy").replace("{percent}", n(data.better_percent!))}
            </p>
          </div>
          {geometry && (
            <>
              <svg
                className="record-comparison-chart"
                viewBox="0 0 360 230"
                role="img"
                aria-label={tr("chart")}
              >
                <path
                  d={`${geometry.outline} Z`}
                  fill="var(--palette-brand-ink)"
                  opacity=".07"
                />
                <path
                  d={geometry.outline}
                  fill="none"
                  stroke="var(--palette-brand-ink)"
                  strokeWidth="1.3"
                  opacity=".25"
                />
                {geometry.bars.map((bar, i) => (
                  <rect
                    key={i}
                    x={bar.x - bar.width / 2}
                    y={176 - bar.height}
                    width={bar.width}
                    height={bar.height}
                    rx={Math.min(4, bar.height / 2)}
                    fill="var(--palette-brand-ink)"
                    opacity={bar.ahead ? 0.82 : 0.35}
                  />
                ))}
                <path d="M18 176H342" stroke="var(--palette-border)" />
                <path
                  d={`M${geometry.marker} 57V178`}
                  stroke="var(--palette-brand-ink)"
                  strokeWidth="2"
                />
                <circle
                  cx={geometry.marker}
                  cy="60"
                  r="5"
                  fill="var(--palette-brand-ink)"
                  stroke="var(--palette-surface)"
                  strokeWidth="2"
                />
                <rect
                  x={bubbleX}
                  y="3"
                  width="112"
                  height="43"
                  rx="13"
                  fill="var(--palette-brand)"
                />
                <path
                  d={`M${
                    Math.max(
                      bubbleX + 12,
                      Math.min(bubbleX + 100, geometry.marker)
                    ) - 5
                  } 45l5 6l5 -6`}
                  fill="var(--palette-brand)"
                />
                <text
                  x={bubbleX + 56}
                  y="19"
                  textAnchor="middle"
                  fill="var(--palette-on-brand)"
                  fontSize="11"
                >
                  {tr("youShort")}
                </text>
                <text
                  x={bubbleX + 56}
                  y="36"
                  textAnchor="middle"
                  fill="var(--palette-on-brand)"
                  fontSize="13"
                  fontWeight="700"
                >
                  {value(data.own_value!)} {unit}
                </text>
                {geometry.ticks.map((tick, i) => (
                  <React.Fragment key={i}>
                    <path
                      d={`M${tick.x} 176v5`}
                      stroke="var(--palette-border)"
                    />
                    <text
                      x={tick.x}
                      y="199"
                      textAnchor={
                        i === 0 && geometry.ticks.length > 1
                          ? "start"
                          : i === geometry.ticks.length - 1 &&
                            geometry.ticks.length > 1
                          ? "end"
                          : "middle"
                      }
                      fill="var(--palette-text-muted)"
                      fontSize="11"
                    >
                      {value(tick.value)}
                    </text>
                  </React.Fragment>
                ))}
                <text
                  x="180"
                  y="224"
                  textAnchor="middle"
                  fill="var(--palette-text-muted)"
                  fontSize="11"
                >
                  {unit}
                </text>
              </svg>
              <div className="record-comparison-axis">
                <span>{tr("lower")}</span>
                <span>{tr("higher")}</span>
              </div>
              <p className="record-comparison-legend">
                <i aria-hidden="true" />
                {tr("barLegend")}
              </p>
            </>
          )}
          {!!data.equal_percent && (
            <p className="record-comparison-caption">
              {tr("ties").replace("{percent}", n(data.equal_percent))}
            </p>
          )}
        </>
      )}
      <details className="record-comparison-guide"><summary aria-label={i18n.language.startsWith("fa") ? "راهنمای مقایسه" : "Comparison guide"}>ⓘ</summary><p className="record-comparison-note">{tr("note")}</p></details>
    </section>
  );
}
